import React, { useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import cn, { Argument as ClassName } from 'classnames'
import { useMediaPredicate } from 'react-media-hook'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { WidgetLayout } from '../../../lib/widget-layout'
import useText from '../../../common/hooks/useText'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { STableLink } from '../../../lib/table/Table.styles'
import {
    CompanyPreview,
    PagedResultOfLoadBalancerGroup,
    LoadBalancerGroupGetAllPagedApiArg,
    LoadBalancerGroup,
} from '../../../common/redux/api/web-api'
import { Button } from '../../../common/components/Button'
import { LoadbalancerGroupModal } from '../../../common/features/loadbalancer-group/loadbalancer-group-modal'
import { ChargersPagination } from '../../../lib/chargers-pagination'
import { ResultsPerPage } from '../../../lib/results-per-page'
import Input from '../../../common/components/Input'
import { isAdminDomain } from '../../../lib/currentDomain'
import { RootState } from '../../redux/store'
import { Combobox } from '../../../common/components/combobox'

export const SButtonGroup = styled.span`
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    margin-top: 5px;
`
export type LoadbalancerTableListWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    loadBalancerGroups: PagedResultOfLoadBalancerGroup
    companies?: CompanyPreview[]
    removeLBGroup: (value: number) => void
    filterSubmit: (values: Partial<LoadBalancerGroupGetAllPagedApiArg>) => void
    filter: LoadBalancerGroupGetAllPagedApiArg
    groupsLoading?: boolean
    setFilter: (filter: Partial<LoadBalancerGroupGetAllPagedApiArg>) => void
}

export const LoadbalancerTableListWidget = React.memo(function LoadbalancerTableListWidget({
    className,
    style,
    loadBalancerGroups,
    companies,
    removeLBGroup,
    filterSubmit,
    filter,
    setFilter,
    groupsLoading,
}: LoadbalancerTableListWidgetProps): React.ReactElement {
    const text = useText()
    const [openAddModal, setOpenAddModal] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState<number>(0)
    const totalCount = loadBalancerGroups?.totalCount
    const pageSize = loadBalancerGroups?.pageSize
    const offset = pageSize && currentPage * pageSize

    const filtersContainerRef = useRef<HTMLDivElement | null>(null)

    const handlePageClick = (pageNumber: number) => {
        window.scrollTo(0, filtersContainerRef.current?.offsetTop || 0)
        setFilter({ ...filter, page: pageNumber })
    }

    const { register, handleSubmit, control, reset } = useForm<LoadBalancerGroupGetAllPagedApiArg>({
        defaultValues: filter,
    })

    const onSelectNumberOfItems = (noOfItems: number) => {
        setFilter({ ...filter, noOfItems, page: 0 })
    }
    const isMobileScreen = useMediaPredicate('(max-width: 600px)')

    const allowAccess = useSelector((state: RootState) => state.user.isSuperAdmin || state.user.isPartner)
    const userId = useSelector((state: RootState) => state.user.id)

    const companyOptions = [
        { label: '', value: '' },
        ...(companies
            ? companies.map((company) => ({
                  value: company?.name,
                  label: company?.name,
              }))
            : []),
    ]

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblLoadbalancerGroups')}
            headerButtons={[
                <span key={userId}>
                    {!isAdminDomain ? (
                        <Button label={text('btnAdd')} size="sm" onClick={() => setOpenAddModal(true)}></Button>
                    ) : null}
                </span>,
            ]}
        >
            <div className="margin-top-med margin-bottom-large">
                <form
                    name="form"
                    role="form"
                    noValidate
                    onSubmit={handleSubmit((values) => {
                        filterSubmit(values)
                        setCurrentPage(0)
                    })}
                >
                    <div className="flex gap-4 flex-wrap">
                        {!isMobileScreen && (
                            <div className="flex-1">
                                <Input
                                    labelText={text('lblId')}
                                    {...register('id')}
                                    data-testid="input-lb-id"
                                    id="id"
                                    className="search-input"
                                />
                            </div>
                        )}
                        <div className="flex-1">
                            <Input
                                labelText={text('lblName')}
                                {...register('name')}
                                data-testid="input-lb-name"
                                id="lbName"
                                {...register('name')}
                                className="search-input"
                            />
                        </div>
                        {!isAdminDomain && (
                            <div className="flex-1">
                                <Controller
                                    name="companyName"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Combobox
                                            id="organisation"
                                            labelText={text('lblOrganisation')}
                                            options={companyOptions}
                                            onChange={(option) => {
                                                onChange(option.value)
                                            }}
                                            enableDefaultOption={false}
                                            data-testid="company-input"
                                            disabledReason={'lblLoadBalancerHasChargePoints'}
                                            value={value}
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                    <div className="flex justify-content-end gap-4 mt-5">
                        <Button
                            label={text('btnSearch')}
                            size={isMobileScreen ? 'xs' : 'sm'}
                            data-testid="btn-search"
                            loading={groupsLoading}
                            type="submit"
                        ></Button>
                        <Button
                            label={text('btnClear')}
                            size={isMobileScreen ? 'xs' : 'sm'}
                            data-testid="btn-clear"
                            onClick={() => reset()}
                            type="submit"
                        ></Button>
                    </div>
                </form>
            </div>
            <div className="mb-4">
                <span>
                    <span className="heading5">{text('msgLbgroupsFound', { amount: totalCount })}</span>
                </span>
                <ResultsPerPage onSelectNumberOfItems={onSelectNumberOfItems} dataTestid="lb-page-result" />
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        {!isMobileScreen && <TableHeaderCell>{text('lblId')}</TableHeaderCell>}
                        <TableHeaderCell>{text('lblName')}</TableHeaderCell>

                        {!isAdminDomain && <TableHeaderCell>{text('lblOrganisation')}</TableHeaderCell>}
                        <TableHeaderCell></TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!groupsLoading &&
                        loadBalancerGroups?.items?.slice(offset, offset + pageSize)?.map((group: LoadBalancerGroup) => (
                            <TableRow key={group?.id}>
                                {!isMobileScreen && (
                                    <TableBodyCell>
                                        <STableLink as={Link} to={`/loadbalancer/${group?.id}`}>
                                            {group?.id}
                                        </STableLink>
                                    </TableBodyCell>
                                )}
                                <TableBodyCell>
                                    {!isMobileScreen ? (
                                        group?.name
                                    ) : (
                                        <STableLink as={Link} to={`/loadbalancer/${group?.id}`}>
                                            {group?.name}
                                        </STableLink>
                                    )}
                                </TableBodyCell>
                                {!isAdminDomain && <TableBodyCell>{group?.companyName}</TableBodyCell>}
                                <TableBodyCell>
                                    <button
                                        disabled={group?.active || !allowAccess}
                                        type="button"
                                        className="btn btn-danger btn-xs margin-left-small btn-icon"
                                        data-testid={
                                            group?.active
                                                ? `disabled-remove-${group?.id}`
                                                : `enabled-remove-${group?.id}`
                                        }
                                        onClick={() => {
                                            removeLBGroup(group?.id ?? 0)
                                        }}
                                        title={(() => {
                                            if (group?.active) {
                                                return text('msgLbActive')
                                            } else if (!allowAccess) {
                                                return text('msgDeniedForNonAdministratorsLBgroup')
                                            } else {
                                                return undefined
                                            }
                                        })()}
                                    >
                                        <span data-testid="remove-icon" className="fa fa-remove"></span>
                                    </button>
                                </TableBodyCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            {loadBalancerGroups?.items?.length !== 0 && totalCount && totalCount > 20 && !groupsLoading && (
                <ChargersPagination
                    setPageIndex={handlePageClick}
                    totalCount={totalCount}
                    pageSize={pageSize ?? 0}
                    pageIndex={filter?.page ?? 0}
                    displayedChargers={pageSize ?? 0}
                />
            )}
            {openAddModal && (
                <LoadbalancerGroupModal closeModal={() => setOpenAddModal(false)} organisations={companies} />
            )}
        </WidgetLayout>
    )
})
